import { useCallback, useSyncExternalStore } from 'react'

import { isEnabled, setEnabled, clearOverride } from './flags'

/**
 * Subscribing to a flag (T-029).
 *
 * The flag lives in localStorage, which React cannot observe, so the write path
 * announces itself. Without this the switch would need a full page reload to
 * take effect — which is exactly the delay the flag exists to avoid, since the
 * salesperson flipping it is mid-sale with a customer waiting.
 *
 * `storage` events are also listened for, so a second tab open on the same
 * counter PC follows along rather than sitting on the other screen until it is
 * next refreshed.
 */

const listeners = new Set()

function announce() {
    listeners.forEach((fn) => fn())
}

function subscribe(fn) {
    listeners.add(fn)
    window.addEventListener('storage', fn)
    return () => {
        listeners.delete(fn)
        window.removeEventListener('storage', fn)
    }
}

/**
 * @param {string} flag one of FLAGS
 * @returns {[boolean, (enabled: boolean|null) => void]} the current value, and a
 *          setter. Passing null clears the override and returns to the build
 *          default.
 */
export function useFeature(flag) {
    const value = useSyncExternalStore(
        subscribe,
        () => isEnabled(flag),
        // Server snapshot. There is no SSR here, but useSyncExternalStore reads
        // it during hydration-shaped renders in tests, and localStorage is the
        // same object in both.
        () => isEnabled(flag)
    )

    const set = useCallback((next) => {
        if (next === null) clearOverride(flag)
        else setEnabled(flag, next)
        announce()
    }, [flag])

    return [value, set]
}

export default useFeature
